<template>
  <div class="w-full flex-1-0-auto header-height-top-space">
    <div class="w-full pb-4 xl:pb-6">
      <div class="mb-8 md:px-6 lg:px-8 xl:px-20 xl:mb-12">
        <div class="container">
          <StepNavigation
            :step1="tr('pages.sales.steps.step1')"
            :step2="tr('pages.sales.steps.step2')"
            :step3="tr('pages.sales.steps.step3')"
            :active-step="2"
          />
        </div>
      </div>

      <div class="w-full viewport-gutter">
        <div class="container">
          <div class="text-sm xl:text-lg">
            <h1 class="pb-3 text-3xl font-bold leading-none xl:text-3.25xl uppercase text-sn-secondary">
              {{ tr('pages.sales.checkout.title') }}
            </h1>
          </div>
        </div>
      </div>
    </div>

    <form
      class="w-full pt-6 pb-16 viewport-gutter xl:pt-9 xl:pb-24"
      @submit.prevent="onSubmit"
    >
      <div
        v-if="isLoading && !hasErrors"
        class="flex flex-col items-center justify-center h-full text-sm text-center xl:text-lg"
      >
        <span class="text-center loading-text">
          {{ tr('pages.sales.checkout.loading.title' ) }}
        </span>

        <div id="lh-loading" class="relative flex-shrink-0 w-full h-3 mt-4 bullets-9">
          <div id="lh-loading-1" class="absolute top-0 w-3 h-3 lh-loading" />
          <div id="lh-loading-2" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-3" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-4" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-5" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-6" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-7" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-8" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-9" class="absolute w-3 h-3 lh-loading" />
        </div>
        <p class="max-w-sm pt-6 mx-auto text-center xl:pt-12">
          {{ tr('pages.sales.checkout.loading.description' ) }}
        </p>
      </div>

      <div
        v-show="!isLoading"
        class="container xl:flex xl:justify-between"
      >
        <div class="w-full space-y-6 xl:max-w-170 xl:mr-5">
          <div id="payment-personal-data" class="w-full p-7 bg-sn-gray-100">
            <p class="pb-4 font-bold md:text-2.5xl md:pb-6 text-sn-gray-900">
              {{ tr('pages.sales.checkout.form.personal.title' ) }}
            </p>

            <div class="space-y-6 xl:space-y-8">
              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.billingFirstName"
                  name="first-name"
                  :label="tr('components.flightVoucher.form.firstName.name')"
                  :placeholder="tr('components.flightVoucher.form.firstName.placeholder')"
                  :error="$v.formFields.billingFirstName.$error ? tr('errors.type.required') : ''"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  autocomplete="given-name"
                  @change="updateDefaults()"
                />
                <TextInput
                  v-model.trim="formFields.billingLastName"
                  name="last-name"
                  :label="tr('components.flightVoucher.form.lastName.name')"
                  :placeholder="tr('components.flightVoucher.form.lastName.placeholder')"
                  :error="$v.formFields.billingLastName.$error ? tr('errors.type.required') : ''"
                  extra-class="md:w-1/2"
                  autocomplete="family-name"
                  @change=" updateDefaults()"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.email"
                  name="email"
                  :label="tr('components.flightVoucher.form.email.name')"
                  :placeholder="tr('components.flightVoucher.form.email.placeholder')"
                  :error="$v.formFields.email.$error ? tr(`errors.type.${emailErrorType}`) : ''"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  autocomplete="email"
                  @change="updateDefaults();"
                />
                <TextInput
                  v-model.trim="formFields.emailConfirmation"
                  name="email-confirmation"
                  :label="tr('components.flightVoucher.form.emailConfirmation.name')"
                  :placeholder="tr('components.flightVoucher.form.emailConfirmation.placeholder')"
                  :error="$v.formFields.emailConfirmation.$error ? tr(`errors.type.${emailConfirmationErrorType}`) : ''"
                  extra-class="md:w-1/2"
                  autocomplete="email"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.street"
                  name="street"
                  :error="$v.formFields.street.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.street.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.street.placeholder')"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  autocomplete="address-line1"
                />
                <TextInput
                  v-model.trim="formFields.zipCode"
                  name="zipCode"
                  :error="$v.formFields.zipCode.$error ? tr(`errors.type.required`) : ''"
                  extra-class="w-full md:w-1/2"
                  :label="tr('pages.sales.checkout.form.personal.zip.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.zip.placeholder')"
                  autocomplete="postal-code"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.city"
                  name="city"
                  :error="$v.formFields.city.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.city.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.city.placeholder')"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  autocomplete="address-level2"
                />
                <TextInput
                  v-model="formFields.country"
                  name="country"
                  :error="$v.formFields.country.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.country.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.country.placeholder')"
                  extra-class="w-full md:w-1/2"
                  autocomplete="country"
                />
              </div>
            </div>
          </div>

          <div v-show="hasErrors" ref="error">
            <Alert
              v-for="error in errors"
              :key="error"
              :title="tr(`errors.type.${error}.title`)"
              :message="tr(`errors.type.${error}.description`)"
            />
          </div>

          <div id="payment-datatrans-form" class="w-full p-7 bg-sn-gray-100">
            <div class="pb-6 space-y-2 md:pb-9 text-sn-gray-900">
              <p class="font-bold md:text-2.5xl">
                {{ tr('pages.sales.checkout.form.payment.title') }}
              </p>

              <p class="text-sm leading-snug md:text-base">
                {{ tr('pages.sales.checkout.form.payment.description') }}
              </p>
            </div>

            <Card3dsInformationForm
              :locked="cardholderLocked"
              :default-name="formFields.cardholder.name || defaultCardholderName"
              :default-email="formFields.cardholder.email || defaultCardholderEmail"
              @unlock="onUnlock"
              @change="onChangeCardholder"
            />

            <div class="space-y-4 xl:space-y-8">
              <div v-show="cardholderLocked && secureFieldsReady" class="flex flex-col md:flex-row md:justify-between">
                <div
                  :class="{ 'is-active': secureFieldsStatus.cardNumber.focused || secureFieldsStatus.cardNumber.filled, 'is-error': secureFieldsStatus.cardNumber.error }"
                  class="w-full input-component-secure-field md:w-1/2 md:mr-10 xl:mr-6"
                >
                  <div class="relative input">
                    <div id="card-number" />
                  </div>

                  <p v-if="shouldCardNumberDisplayError" class="error">
                    <span class="inline-flex px-2 py-1 mt-1 text-xs leading-normal text-white input-error bg-sn-error">{{ tr(`errors.type.invalid`) }}</span>
                  </p>
                </div>

                <div class="flex justify-between md:w-1/2 mt-4 md:mt-0">
                  <TextInput
                    v-model="formFields.expiryDate"
                    v-mask="'##/##'"
                    :error="$v.formFields.expiryDate.$error ? tr(`errors.type.${expiryDateErrorType}`) : ''"
                    :label="tr('pages.sales.checkout.form.payment.expiryDate.name')"
                    :placeholder="tr('pages.sales.checkout.form.payment.expiryDate.placeholder')"
                    extra-class="w-8/12 mr-5"
                  />

                  <div
                    :class="{ 'is-active': secureFieldsStatus.cvv.focused || secureFieldsStatus.cvv.filled, 'is-error': shouldCvvDisplayError }"
                    class="w-full input-component-secure-field"
                  >
                    <div class="relative input">
                      <div id="cvv-number" />
                    </div>

                    <p v-if="shouldCvvDisplayError" class="error">
                      <span class="inline-flex px-2 py-1 mt-1 text-xs leading-normal text-white input-error bg-sn-error">{{ tr('errors.type.invalid') }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-shrink-0 w-full payment-summary xl:w-120 xl:flex">
          <ScrollWagon
            class="p-7 xl:w-120 bg-sn-gray-100"
            top-limit-id="payment-personal-data"
            bottom-limit-id="payment-datatrans-form"
            :offset="120"
          >
            <VoucherAmountSummary class="pb-10 mb-9 border-b border-sn-gray-300" :extended="true" />

            <div class="mb-10">
              <label class="flex cursor-pointer">
                <input
                  v-model="formFields.terms"
                  name="terms"
                  type="checkbox"
                  class="w-5 h-5 mt-1 text-white border border-sn-primary form-checkbox form-checkbox-sn"
                  :class="{ 'border-red is-error': $v.formFields.terms.$error }"
                >
                <i18n path="pages.sales.checkout.terms" class="ml-4 pt-0.5" tag="p">
                  <template #link>
                    <router-link
                      class="underline"
                      :to="$i18nTo({ name: 'termsOfUse'})"
                      target="_blank"
                    >{{ tr('pages.sales.checkout.termsLink') }}</router-link>
                  </template>
                </i18n>
              </label>

              <i18n path="pages.sales.checkout.terms.privacyPolicy" class="ml-9" tag="p">
                <template #link>
                  <a
                    class="underline"
                    target="_blank"
                    :href="privacyLink"
                  >{{ tr('pages.sales.checkout.terms.privacyPolicyLink') }}</a>
                </template>
              </i18n>
            </div>

            <!-- Hidden until a legally approved promotion text is available (#4324)
            <div class="mb-10">
              <label class="flex cursor-pointer">
                <input
                  v-model="formFields.marketingConsent"
                  name="marketingConsent"
                  type="checkbox"
                  class="w-5 h-5 mt-1 text-white border border-sn-primary form-checkbox form-checkbox-sn"
                >
                <i18n path="pages.sales.checkout.marketingConsent" class="ml-4 pt-0.5" tag="p">
                  <template #link>
                    <a
                      class="underline"
                      target="_blank"
                      :href="promotionTermsLink"
                    >{{ tr('pages.sales.checkout.marketingConsentLink') }}</a>
                  </template>
                </i18n>
              </label>
            </div>
            -->

            <div v-if="$v.formFields.terms.$error" class="mb-10 -mt-5 text-red">
              <span
                class="text-white text-xs inline-flex px-2 py-1 mt-2 leading-normal input-error bg-sn-error"
              >
                {{ tr('pages.sales.checkout.termsPrivacyError') }}

              </span>
            </div>

            <div class="relative w-full">
              <div class="max-w-70">
                <Button
                  variant="primary"
                  :loading="isLoading"
                  :disabled="!cardholderLocked || !secureFieldsReady"
                  full-width
                  type="submit"
                >
                  {{ tr('pages.sales.checkout.button.purchase') }}
                </Button>
              </div>
            </div>
          </ScrollWagon>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Card3dsInformationForm from '~brand-components/brussels/sale/card-3ds-information-form.vue'
import VoucherSaleCheckout from '~brand-pages/base/voucher-sale-checkout'
import TextInput from '~brand-components/brussels/shared/text-input.vue'
import StepNavigation from '~brand-components/brussels/navigation/step-navigation.vue'
import Alert from '~brand-components/brussels/alert/alert.vue'
import Button from '~brand-components/brussels/button/button.vue'
import VoucherAmountSummary from '~brand-components/brussels/sale/voucher-amount-summary.vue'
import brandConfig from '~branding/brussels/config'

const SECURE_FIELDS_STYLES = {
  '*': `
    width: 100%;
    font-size: 1rem;
    color: #000000;
    background-color: white;
    height: 3rem;
    padding: 0 1rem;
    margin: 0;
    border: 1px solid #c5c5c5;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    outline: 0;
  `,
  '*:focus': `
    border-color: #CAD1D9;
  `,
  '*::placeholder': `
    color: #757575;
  `,
  '*.invalid': `
    border: 1px solid #ac0020;
  `,
  '*.valid': `
    border: 1px solid #c5c5c5;
  `,
  '*.empty': `
    border: 1px solid #c5c5c5;
  `,
}

export default VoucherSaleCheckout.extend({
  components: {
    Card3dsInformationForm,
    TextInput,
    StepNavigation,
    Alert,
    Button,
    VoucherAmountSummary,
  },
  computed: {
    brandConfig() {
      return brandConfig
    },
    secureFieldStyles() {
      return SECURE_FIELDS_STYLES
    },
    privacyLink() {
      if (this.$i18n.locale === 'nl') {
        return 'https://www.brusselsairlines.com/xx/nl/legal-notices/data-protection-and-privacy-notice'
      }

      if (this.$i18n.locale === 'fr') {
        return 'https://www.brusselsairlines.com/xx/fr/legal-notices/data-protection-and-privacy-notice'
      }

      return 'https://www.brusselsairlines.com/xx/en/legal-notices/data-protection-and-privacy-notice'
    },
    promotionTermsLink() {
      return 'https://brussels-giftvoucher.vp-lhg.com/terms-of-use'
    },
  },
})
</script>

<style lang="scss" scoped>
.border-red {
  border-color: #8c1e33;
}

.input-component-secure-field {
  .input {
    height: 3rem;
  }
}
</style>
